// apps/client/src/components/clientSets/ClientSetDetailDialog.tsx
import React, { useEffect, useState } from 'react';
import { useClientSetDetails } from '../../hooks/useClientSetDetails';
import { Spinner } from '../common/Spinner';

interface ClientSetDetailDialogProps {
  clientSetId: string;
  clientSetName: string;
  onClose: () => void;
}

export const ClientSetDetailDialog: React.FC<ClientSetDetailDialogProps> = ({
  clientSetId,
  clientSetName,
  onClose,
}) => {
  const { details, loading, error, fetchDetails } = useClientSetDetails();
  const [showAllLoans, setShowAllLoans] = useState(false);

  useEffect(() => {
    fetchDetails(clientSetId);
  }, [clientSetId]);

  const loansToShow = showAllLoans ? details?.loans || [] : (details?.loans || []).slice(0, 10);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800 z-10">
          <h2 className="text-2xl font-bold text-white">{clientSetName}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Details */}
          {!loading && !error && details && (
            <>
              {/* Customer Names */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">
                  Customer Names ({details.clientSet.customerNames.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {details.clientSet.customerNames.map((name, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                    >
                      {name}
                    </div>
                  ))}
                </div>
              </div>

              {/* Portfolio Summary */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Portfolio Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 min-h-[80px]">
                    <div className="text-gray-400 text-sm mb-1 whitespace-nowrap">Total Loans</div>
                    <div className="text-white text-xl font-bold">
                      {details.summary.totalLoans}
                    </div>
                  </div>
                  <div className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 min-h-[80px]">
                    <div className="text-gray-400 text-sm mb-1 whitespace-nowrap">Loan Book</div>
                    <div className="text-white text-xl font-bold whitespace-nowrap">
                      ${(details.summary.totalLoanBook || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                  <div className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 min-h-[80px]">
                    <div className="text-gray-400 text-sm mb-1 whitespace-nowrap">Lifetime</div>
                    <div className="text-white text-xl font-bold whitespace-nowrap">
                      ${details.summary.lifetimeCommission.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                  <div className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 min-h-[80px]">
                    <div className="text-gray-400 text-sm mb-1 whitespace-nowrap">Upfront</div>
                    <div className="text-white text-xl font-bold whitespace-nowrap">
                      ${details.summary.upfrontCommission.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                  <div className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 min-h-[80px]">
                    <div className="text-gray-400 text-sm mb-1 whitespace-nowrap">Trail</div>
                    <div className="text-white text-xl font-bold whitespace-nowrap">
                      ${details.summary.trailCommission.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Loans Table */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-white">
                    All Loans ({details.loans.length})
                  </h3>
                  {details.loans.length > 10 && (
                    <button
                      onClick={() => setShowAllLoans(!showAllLoans)}
                      className="text-blue-500 hover:text-blue-400 text-sm"
                    >
                      {showAllLoans ? 'Show Less' : 'Show All'}
                    </button>
                  )}
                </div>
                <div className="border border-gray-700 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Loan ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Lender
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Settlement
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Loan Amount
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Commission
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {loansToShow.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                              No loans found
                            </td>
                          </tr>
                        ) : (
                          loansToShow.map((loan, index) => (
                            <tr key={index} className="hover:bg-gray-900 transition-colors">
                              <td className="px-4 py-3 text-sm text-white">{loan.loanId}</td>
                              <td className="px-4 py-3 text-sm text-white">{loan.customerName}</td>
                              <td className="px-4 py-3 text-sm text-gray-300">{loan.lender}</td>
                              <td className="px-4 py-3 text-sm text-gray-300">
                                {loan.settlementDate ? new Date(loan.settlementDate).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white text-right">
                                ${loan.loanAmount.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </td>
                              <td className="px-4 py-3 text-sm text-white text-right">
                                ${loan.totalCommission.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {!showAllLoans && details.loans.length > 10 && (
                  <div className="text-center mt-2 text-sm text-gray-400">
                    Showing 10 of {details.loans.length} loans
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
